import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Plus, Upload, FolderSimple, Image, Video, FileAudio, FileText, File,
  MagnifyingGlass, Trash, Tag, X, CloudArrowUp, CheckCircle, CircleNotch
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const folders = [
  { id: "all", name: "All Files", icon: FolderSimple },
  { id: "videos", name: "Lesson Videos", icon: Video },
  { id: "clips", name: "Promo Clips", icon: Video },
  { id: "audio", name: "Voice Files", icon: FileAudio },
  { id: "images", name: "Photos", icon: Image },
  { id: "thumbnails", name: "Thumbnails", icon: Image },
  { id: "documents", name: "PDFs & Ebooks", icon: FileText },
  { id: "avatars", name: "Avatars", icon: Image },
  { id: "recordings", name: "Screen Recordings", icon: Video },
];

const getFileIcon = (fileType) => {
  if (fileType === 'video' || fileType?.startsWith('video')) return Video;
  if (fileType === 'audio' || fileType?.startsWith('audio')) return FileAudio;
  if (fileType === 'image' || fileType?.startsWith('image')) return Image;
  if (fileType === 'document' || fileType?.includes('pdf')) return FileText;
  if (fileType === 'slides') return FileText;
  return File;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MediaCard = ({ item, onDelete, token }) => {
  const Icon = getFileIcon(item.file_type);
  const [imageUrl, setImageUrl] = useState(null);
  
  useEffect(() => {
    // For images with storage_path, fetch as blob
    if (item.file_type === 'image' && item.storage_path && token) {
      const fetchImage = async () => {
        try {
          const response = await axios.get(`${API}/media/${item.id}/download`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          });
          const blobUrl = URL.createObjectURL(response.data);
          setImageUrl(blobUrl);
        } catch (err) {
          console.error("Failed to load image:", err);
        }
      };
      fetchImage();
      
      return () => {
        if (imageUrl) URL.revokeObjectURL(imageUrl);
      };
    }
  }, [item, token]);
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="media-item group"
    >
      <div className="relative h-full flex flex-col">
        {/* Preview area */}
        <div className="flex-1 flex items-center justify-center bg-zinc-800/50 p-4 min-h-[120px]">
          {imageUrl ? (
            <img src={imageUrl} alt={item.name} className="max-w-full max-h-full object-contain rounded" />
          ) : item.url && item.file_type === 'image' ? (
            <img src={item.url} alt={item.name} className="max-w-full max-h-full object-contain rounded" />
          ) : (
            <Icon size={48} className="text-zinc-500" weight="duotone" />
          )}
        </div>
        
        {/* Info */}
        <div className="p-3">
          <p className="text-sm text-white truncate">{item.name}</p>
          <p className="text-xs text-zinc-500">{formatFileSize(item.size_bytes || 0)}</p>
        </div>

        {/* Hover actions */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button
            onClick={() => onDelete(item.id)}
            className="p-2 rounded-lg bg-rose-500/20 text-rose-400 hover:bg-rose-500/30 transition-colors"
          >
            <Trash size={18} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default function MediaLibrary() {
  const [media, setMedia] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Upload form state
  const [uploadFolder, setUploadFolder] = useState("images");
  const [uploadTags, setUploadTags] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  // Get auth token
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchMedia();
  }, [selectedFolder]);

  const fetchMedia = async () => {
    try {
      const params = selectedFolder !== 'all' ? { folder: selectedFolder } : {};
      const res = await axios.get(`${API}/media`, { params });
      setMedia(res.data);
    } catch (err) {
      toast.error("Failed to fetch media");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(files);
      setShowUpload(true);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error("Please select files to upload");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    let successCount = 0;
    const totalFiles = selectedFiles.length;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', uploadFolder);
        formData.append('tags', uploadTags);

        const res = await axios.post(`${API}/media/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const fileProgress = (progressEvent.loaded / progressEvent.total) * 100;
            const overallProgress = ((i + fileProgress / 100) / totalFiles) * 100;
            setUploadProgress(Math.round(overallProgress));
          }
        });

        // Add to media list
        setMedia(prev => [{
          ...res.data,
          storage_path: res.data.storage_path,
          folder: uploadFolder,
          tags: uploadTags.split(',').map(t => t.trim()).filter(Boolean)
        }, ...prev]);
        
        successCount++;
      } catch (err) {
        console.error("Upload error:", err);
        toast.error(`Failed to upload ${file.name}: ${err.response?.data?.detail || err.message}`);
      }
    }

    setUploading(false);
    setUploadProgress(100);
    
    if (successCount > 0) {
      toast.success(`${successCount} file(s) uploaded successfully!`);
    }
    
    resetUploadForm();
    setShowUpload(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/media/${id}`);
      setMedia(media.filter(m => m.id !== id));
      toast.success("Media deleted");
    } catch (err) {
      toast.error("Failed to delete media");
    }
  };

  const resetUploadForm = () => {
    setUploadFolder("images");
    setUploadTags("");
    setSelectedFiles([]);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      setSelectedFiles(files);
      setShowUpload(true);
    }
  }, []);

  // Filter media by search
  const filteredMedia = media.filter(m => 
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div data-testid="media-library">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,audio/*,.pdf,.ppt,.pptx,.doc,.docx"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Media Library
          </h1>
          <p className="text-zinc-400">Manage your assets and content files</p>
        </div>
        <Button
          onClick={() => fileInputRef.current?.click()}
          data-testid="upload-media-btn"
          className="h-12 px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold"
        >
          <Upload size={20} className="mr-2" />
          Upload Files
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Sidebar - Folders */}
        <div className="w-56 shrink-0">
          <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-4">
            <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3">Folders</h3>
            <nav className="space-y-1">
              {folders.map(folder => {
                const Icon = folder.icon;
                const isActive = selectedFolder === folder.id;
                const count = folder.id === 'all' 
                  ? media.length 
                  : media.filter(m => m.folder === folder.id).length;

                return (
                  <button
                    key={folder.id}
                    onClick={() => setSelectedFolder(folder.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-xl transition-colors ${
                      isActive ? 'bg-amber-500/10 text-amber-500' : 'text-zinc-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <Icon size={18} />
                      <span className="text-sm">{folder.name}</span>
                    </span>
                    <span className="text-xs">{count}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search files by name or tag..."
                data-testid="media-search-input"
                className="pl-12 h-12 bg-zinc-900/50 border-white/5 rounded-xl"
              />
            </div>
          </div>

          {/* Drop Zone / Grid */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`min-h-[400px] rounded-2xl transition-all ${isDragging ? 'bg-amber-500/10 border-2 border-dashed border-amber-500' : ''}`}
          >
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredMedia.length === 0 ? (
              <div 
                className="upload-zone p-12 text-center cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudArrowUp size={64} className="mx-auto mb-4 text-amber-500" weight="duotone" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  {searchQuery ? 'No files found' : 'Upload your first file'}
                </h3>
                <p className="text-zinc-400 mb-6">
                  {searchQuery ? 'Try a different search term' : 'Drag and drop files here or click to browse'}
                </p>
                <p className="text-xs text-zinc-500">
                  Supports: Images, Videos, Audio, PDFs, Slides (Max 100MB)
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {filteredMedia.map((item) => (
                  <MediaCard key={item.id} item={item} onDelete={handleDelete} token={token} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={(open) => {
        if (!uploading) {
          setShowUpload(open);
          if (!open) resetUploadForm();
        }
      }}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Upload Files
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            {/* Selected Files */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Selected Files ({selectedFiles.length})</Label>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {selectedFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-zinc-800 rounded-xl">
                    {file.type.startsWith('image') && <Image size={20} className="text-emerald-400" />}
                    {file.type.startsWith('video') && <Video size={20} className="text-blue-400" />}
                    {file.type.startsWith('audio') && <FileAudio size={20} className="text-violet-400" />}
                    {!file.type.startsWith('image') && !file.type.startsWith('video') && !file.type.startsWith('audio') && (
                      <File size={20} className="text-zinc-400" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{file.name}</p>
                      <p className="text-xs text-zinc-500">{formatFileSize(file.size)}</p>
                    </div>
                    {!uploading && (
                      <button
                        onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== idx))}
                        className="p-1 text-zinc-400 hover:text-rose-400"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              {!uploading && (
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="w-full h-10 rounded-xl border-dashed border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-600"
                >
                  <Plus size={16} className="mr-2" />
                  Add More Files
                </Button>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Folder</Label>
              <Select value={uploadFolder} onValueChange={setUploadFolder} disabled={uploading}>
                <SelectTrigger className="h-12 bg-zinc-950 border-white/10 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {folders.filter(f => f.id !== 'all').map(folder => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Tags (comma separated)</Label>
              <Input
                value={uploadTags}
                onChange={(e) => setUploadTags(e.target.value)}
                placeholder="lesson, thumbnail, promo"
                disabled={uploading}
                className="h-12 bg-zinc-950 border-white/10 rounded-xl"
              />
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Uploading...</span>
                  <span className="text-amber-500">{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => {
                  if (!uploading) {
                    setShowUpload(false);
                    resetUploadForm();
                  }
                }}
                disabled={uploading}
                className="flex-1 h-12 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading || selectedFiles.length === 0}
                data-testid="confirm-upload-btn"
                className="flex-1 h-12 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              >
                {uploading ? (
                  <span className="flex items-center gap-2">
                    <CircleNotch size={18} className="animate-spin" />
                    Uploading...
                  </span>
                ) : (
                  <>
                    <CloudArrowUp size={20} className="mr-2" />
                    Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
